import { useSearchParams } from 'react-router';
import { X } from 'lucide-react';
import { PRIORITIES, TASK_TYPES, type Priority, type TaskType } from '@virtuetasks/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useUsers } from '@/features/settings/api';
import { useLabels } from './api';

/**
 * Chip-based filter bar (§8.4). Everything serialises to the URL query string,
 * so a filtered view is shareable and reproduces exactly when pasted.
 */
export const FILTER_KEYS = [
  'q',
  'assigneeId',
  'priority',
  'type',
  'labelId',
  'statusCategory',
  'dueBucket',
  'groupBy',
  'sortBy',
  'sortDir',
  'includeArchived',
] as const;

export type FilterKey = (typeof FILTER_KEYS)[number];

export function useFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const get = (key: FilterKey): string[] => {
    const raw = searchParams.get(key);
    return raw ? raw.split(',').filter(Boolean) : [];
  };

  const set = (key: FilterKey, values: string[]) => {
    const next = new URLSearchParams(searchParams);
    if (values.length === 0) next.delete(key);
    else next.set(key, values.join(','));
    setSearchParams(next, { replace: true });
  };

  const toggle = (key: FilterKey, value: string) => {
    const current = get(key);
    set(key, current.includes(value) ? current.filter((v) => v !== value) : [...current, value]);
  };

  const clearAll = () => setSearchParams(new URLSearchParams(), { replace: true });

  const activeCount = FILTER_KEYS.filter(
    (k) => !['groupBy', 'sortBy', 'sortDir'].includes(k) && get(k).length > 0,
  ).length;

  /** The query object handed straight to the §7.6 endpoint. */
  const toQuery = (): Record<string, unknown> => {
    const query: Record<string, unknown> = {};
    for (const key of FILTER_KEYS) {
      const values = get(key);
      if (values.length) query[key] = values.join(',');
    }
    return query;
  };

  return { get, set, toggle, clearAll, activeCount, toQuery, searchParams };
}

function ChipPopover({
  label,
  values,
  options,
  onToggle,
}: {
  label: string;
  values: string[];
  options: { value: string; label: string; colour?: string }[];
  onToggle: (value: string) => void;
}) {
  const active = values.length > 0;
  const summary = active
    ? options
        .filter((o) => values.includes(o.value))
        .map((o) => o.label)
        .join(', ')
    : label;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={active ? 'subtle' : 'secondary'}
          size="sm"
          className={cn('max-w-[220px]', active && 'text-[var(--accent)]')}
        >
          <span className="truncate">{summary}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="max-h-[300px] overflow-y-auto">
        {options.length === 0 ? (
          <p className="px-2 py-1.5 text-[12px] vt-muted">Nothing to filter by yet</p>
        ) : (
          options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onToggle(option.value)}
              className="flex w-full items-center gap-2 rounded-[4px] px-2 py-1.5 text-left text-[13px] hover:bg-[var(--surface-2)]"
            >
              <input type="checkbox" readOnly checked={values.includes(option.value)} />
              {option.colour && (
                <span className="size-2 rounded-full" style={{ background: option.colour }} aria-hidden />
              )}
              <span className="truncate">{option.label}</span>
            </button>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
}

const DUE_BUCKETS = [
  { value: 'overdue', label: 'Overdue' },
  { value: 'today', label: 'Due today' },
  { value: 'tomorrow', label: 'Due tomorrow' },
  { value: 'this_week', label: 'This week' },
  { value: 'next_week', label: 'Next week' },
  { value: 'no_date', label: 'No due date' },
];

const GROUP_OPTIONS = [
  { value: '', label: 'No grouping' },
  { value: 'status', label: 'Status' },
  { value: 'assignee', label: 'Assignee' },
  { value: 'priority', label: 'Priority' },
  { value: 'project', label: 'Project' },
  { value: 'type', label: 'Type' },
  { value: 'label', label: 'Label' },
];

export function FilterBar({ showGrouping = true }: { showGrouping?: boolean }) {
  const filters = useFilters();
  const { data: people = [] } = useUsers();
  const { data: labels = [] } = useLabels();

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Input
        value={filters.get('q')[0] ?? ''}
        onChange={(e) => filters.set('q', e.target.value ? [e.target.value] : [])}
        placeholder="Search text…"
        aria-label="Search tasks"
        className="h-7 w-[170px]"
      />

      <ChipPopover
        label="Assignee"
        values={filters.get('assigneeId')}
        onToggle={(v) => filters.toggle('assigneeId', v)}
        options={[
          { value: 'me', label: 'Me' },
          { value: 'unassigned', label: 'Unassigned' },
          ...people.filter((p) => p.status === 'active').map((p) => ({ value: p._id, label: p.name })),
        ]}
      />

      <ChipPopover
        label="Priority"
        values={filters.get('priority')}
        onToggle={(v) => filters.toggle('priority', v)}
        options={PRIORITIES.map((p: Priority) => ({ value: p, label: p }))}
      />

      <ChipPopover
        label="Type"
        values={filters.get('type')}
        onToggle={(v) => filters.toggle('type', v)}
        options={TASK_TYPES.map((t: TaskType) => ({ value: t, label: t }))}
      />

      <ChipPopover
        label="Label"
        values={filters.get('labelId')}
        onToggle={(v) => filters.toggle('labelId', v)}
        options={labels.map((l) => ({ value: l._id, label: l.name, colour: l.colour }))}
      />

      <ChipPopover
        label="Due"
        values={filters.get('dueBucket')}
        onToggle={(v) => filters.set('dueBucket', filters.get('dueBucket')[0] === v ? [] : [v])}
        options={DUE_BUCKETS}
      />

      {showGrouping && (
        <select
          className="h-7 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[12px]"
          value={filters.get('groupBy')[0] ?? ''}
          aria-label="Group by"
          onChange={(e) => filters.set('groupBy', e.target.value ? [e.target.value] : [])}
        >
          {GROUP_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label ? `Group: ${o.label}` : 'No grouping'}
            </option>
          ))}
        </select>
      )}

      {filters.activeCount > 0 && (
        <Button variant="ghost" size="sm" onClick={filters.clearAll}>
          <X />
          Clear all
        </Button>
      )}
    </div>
  );
}
